import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { type CSSProperties } from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import { Centered } from '@sorare/core/src/atoms/layout/Centered';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM } from '@sorare/core/src/atoms/typography';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useCommonGameSet } from '@sorare/core/src/set';
import { SeriesBalance } from '@sorare/marketplace/src/components/commonCard/BuyPacks/SeriesBalance';

import { PlayMissions } from '#src/components/missions/PlayMissions/index.tsx';

import type {
  PlayMissionsDialogQuery,
  PlayMissionsDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const PLAY_MISSIONS_DIALOG_QUERY = gql`
  query PlayMissionsDialogQuery($sport: Sport!) {
    currentUser {
      slug
      ...PlayMissions_currentUser
    }
  }
  ${PlayMissions.fragments.currentUser}
` as TypedDocumentNode<
  PlayMissionsDialogQuery,
  PlayMissionsDialogQueryVariables
>;

const HeaderRow = styled(Centered)`
  padding: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-150);
  gap: var(--double-unit);
`;

const HeaderTitle = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  flex: 1;
  justify-content: center;
  color: var(--c-black);
`;

const Body = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
  overflow-y: auto;
  width: 100%;
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const PlayMissionsDialog = ({ open, onClose }: Props) => {
  const commonGameSet = useCommonGameSet();
  const { sport } = useSportContext();
  const { data, refetch } = useQuery(PLAY_MISSIONS_DIALOG_QUERY, {
    variables: { sport },
    skip: !open,
  });

  return (
    <Dialog open={open} onClose={onClose} hideHeader maxWidth="sm" fullWidth>
      <Vertical gap={0}>
        <HeaderRow
          left={<SeriesBalance cardSetSlugs={[commonGameSet.slug]} />}
          right={
            <IconButton
              size="small"
              onClick={onClose}
              icon={iconX}
              color="quaternary"
              aria-label="Close"
            />
          }
        >
          <HeaderTitle>
            <Trapezoid
              style={
                {
                  '--background': 'var(--c-arena-primary)',
                } as CSSProperties
              }
            >
              <Horizontal center gap={0.5}>
                <BrandM>
                  <FormattedMessage {...glossary.play} />
                </BrandM>
              </Horizontal>
            </Trapezoid>
          </HeaderTitle>
        </HeaderRow>
        <Body>
          <PlayMissions
            currentUser={data?.currentUser}
            refetch={() => {
              refetch();
            }}
          />
        </Body>
      </Vertical>
    </Dialog>
  );
};
